import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { normalizeComparableLogo } from '@/components/logo-builder/logo-identity';
import type {
  FooterSlotConfig,
  SiteFooterSlotConfig,
  SiteHeaderSlotConfig,
} from '@/components/slot-settings/types';
import { normalizeFooterElements, normalizeFooterSettings } from '@/hooks/use-page-slot-editor';
import { normalizeButtonConfig } from '@/lib/blocks/button-links';
import { normalizeColorValues } from '@/lib/site-design/colors';
import { DEFAULT_HEADING_FONT, DEFAULT_PARAGRAPH_FONT } from '@/lib/site-design/fonts';
import type { SpecialPageType } from '@/lib/special-page-layouts';
import { PAGE_EDITOR_HISTORY_TARGET_IDS, usePageEditorStore } from '@/stores/page-editor-store';
import { SlotName } from '@/types/page';
import type { ButtonSettings, ColorSettings, FontSettings, SiteLogo } from '@/types/shared';
import {
  readPersistedEditorHistory,
  registerSiteHistoryApplier,
  registerSiteHistoryProvider,
  scheduleEditorHistoryWrite,
  withApplyingPersistedEditorHistory,
} from './editor-history-storage';
import {
  persistDesign,
  persistDraft,
  persistSlot,
  persistSpecialPageLayout,
} from './site-styles-persistence';
import {
  cloneStyles,
  computeJsonChanged,
  emptySiteStylesHistory,
  isEditorWindow,
  normalizeSiteFooter,
  normalizeSiteHeader,
  SITE_HISTORY_LIMIT,
  type SiteStyles,
  type SpecialPageLayoutCodes,
  trimSiteHistory,
} from './site-styles-shared';

export { flushPendingSiteStylesSaves } from './site-styles-persistence';

interface SiteStylesStore {
  original: SiteStyles;
  draft: SiteStyles;
  isSaving: boolean;

  // Per-store undo/redo stacks. Entries are deep clones of the draft at the
  // moment of the change and are popped in normal LIFO order.
  historyPast: SiteStyles[];
  historyFuture: SiteStyles[];
  // Origin target ids parallel to the snapshot stacks. A non-null entry pins
  // the undo/redo highlight to a specific section (e.g. footer when the logo
  // is changed from the footer) instead of inferring it from the diff.
  historyPastTargets: (string | null)[];
  historyFutureTargets: (string | null)[];

  initializeLogo: (logo: SiteLogo | null) => void;
  initializeLogoTagline: (tagline: string | null) => void;
  setDraftLogo: (
    logo: SiteLogo | null,
    options?: {
      captureHistory?: boolean;
    },
  ) => void;
  setDraftLogoTagline: (tagline: string) => void;
  initializeColors: (colors: ColorSettings | null) => void;
  initializePersistedCustomColors: (colors: ColorSettings | null) => void;
  setDraftColors: (colors: ColorSettings | null) => void;
  setPersistedCustomColors: (colors: ColorSettings | null) => void;
  initializeFonts: (fonts: FontSettings | null) => void;
  setDraftFonts: (fonts: FontSettings | null) => void;
  initializeHeader: (header: SiteHeaderSlotConfig | null) => void;
  setDraftHeader: (header: SiteHeaderSlotConfig | null) => void;
  initializeFooter: (footer: SiteFooterSlotConfig | null) => void;
  setDraftFooter: (footer: SiteFooterSlotConfig | null) => void;
  updateDraftFooter: (changes: Partial<SiteFooterSlotConfig>) => void;
  initializeButtons: (buttons: ButtonSettings | null) => void;
  setDraftButtons: (buttons: ButtonSettings | null) => void;
  initializeSpecialPageLayouts: (layouts: SpecialPageLayoutCodes | null) => void;
  setSpecialPageLayout: (pageType: SpecialPageType, code: string) => void;
  commitChanges: () => void;
  setIsSaving: (saving: boolean) => void;

  hasUnsavedChanges: () => boolean;

  historyAccountId: string | null;
  initHistoryScope: (accountId: string) => void;
}

const initialStyles: SiteStyles = {
  logo: null,
  logoTagline: null,
  colors: null,
  persistedCustomColors: null,
  fonts: null,
  header: null,
  footer: null,
  buttons: null,
  specialPageLayouts: null,
};

const layoutKeyFor = (pageType: SpecialPageType): keyof SpecialPageLayoutCodes =>
  pageType === 'case_result' ? 'caseResult' : 'blog';
// Captures the current draft into history before applying a mutation, then
// records a 'site' entry on the page-editor history order so the unified
// undo/redo stack stays chronological with page edits.
const captureSiteHistory = (
  current: SiteStyles,
  originTarget: string | null,
): { past: SiteStyles[]; pastTargets: (string | null)[] } => {
  usePageEditorStore.getState().recordSiteHistoryEntry();
  const state = useSiteStylesStore.getState();
  const past = trimSiteHistory([...state.historyPast, cloneStyles(current)]);
  const rawTargets = [...state.historyPastTargets, originTarget];
  const pastTargets =
    rawTargets.length > SITE_HISTORY_LIMIT
      ? rawTargets.slice(rawTargets.length - SITE_HISTORY_LIMIT)
      : rawTargets;
  return { past, pastTargets };
};

const resolveLogoOriginTarget = (): string => {
  const slot = usePageEditorStore.getState().editingSlot;
  return slot === SlotName.FOOTER
    ? PAGE_EDITOR_HISTORY_TARGET_IDS.footer
    : PAGE_EDITOR_HISTORY_TARGET_IDS.header;
};

const normalizeComparableColors = (colors: ColorSettings | null): ColorSettings | null =>
  colors ? normalizeColorValues(colors) : null;

const initialHistory = emptySiteStylesHistory();

export const useSiteStylesStore = create<SiteStylesStore>((set, get) => ({
  original: { ...initialStyles },
  draft: { ...initialStyles },
  isSaving: false,
  historyPast: initialHistory.historyPast,
  historyFuture: initialHistory.historyFuture,
  historyPastTargets: initialHistory.historyPastTargets,
  historyFutureTargets: initialHistory.historyFutureTargets,
  historyAccountId: null,

  initHistoryScope: (accountId: string) => {
    if (!isEditorWindow) return;
    const previous = get().historyAccountId;
    if (previous === accountId) return;

    const { site: persisted } = readPersistedEditorHistory(accountId);

    withApplyingPersistedEditorHistory(() => {
      set({
        historyAccountId: accountId,
        historyPast: persisted.historyPast,
        historyFuture: persisted.historyFuture,
        historyPastTargets: persisted.historyPastTargets,
        historyFutureTargets: persisted.historyFutureTargets,
      });
    });
  },

  initializeLogo: (logo) =>
    set({
      original: { ...get().original, logo },
      draft: { ...get().draft, logo },
    }),

  initializeLogoTagline: (logoTagline) =>
    set({
      original: { ...get().original, logoTagline },
      draft: { ...get().draft, logoTagline },
    }),

  setDraftLogo: (logo, options = {}) => {
    const { captureHistory = true } = options;
    const current = get().draft;
    if (!computeJsonChanged(current.logo, logo)) return;

    const captured = captureHistory ? captureSiteHistory(current, resolveLogoOriginTarget()) : null;
    const nextDraft = { ...current, logo };

    set({
      draft: nextDraft,
      historyPast: captured ? captured.past : get().historyPast,
      historyPastTargets: captured ? captured.pastTargets : get().historyPastTargets,
      historyFuture: captureHistory ? [] : get().historyFuture,
      historyFutureTargets: captureHistory ? [] : get().historyFutureTargets,
    });
  },

  setDraftLogoTagline: (logoTagline) => {
    const normalizedLogoTagline = logoTagline.trim() || null;
    const current = get().draft;
    if (current.logoTagline === normalizedLogoTagline) return;
    const { past, pastTargets } = captureSiteHistory(current, resolveLogoOriginTarget());
    const nextDraft = { ...current, logoTagline: normalizedLogoTagline };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistDesign(nextDraft, current);
  },

  initializeColors: (colors) =>
    set({
      original: {
        ...get().original,
        colors: normalizeComparableColors(colors),
        persistedCustomColors: null,
      },
      draft: {
        ...get().draft,
        colors: normalizeComparableColors(colors),
        persistedCustomColors: null,
      },
    }),

  initializePersistedCustomColors: (persistedCustomColors) =>
    set({
      original: {
        ...get().original,
        persistedCustomColors: normalizeComparableColors(persistedCustomColors),
      },
      draft: {
        ...get().draft,
        persistedCustomColors: normalizeComparableColors(persistedCustomColors),
      },
    }),

  setDraftColors: (colors) => {
    const current = get().draft;
    const nextColors = normalizeComparableColors(colors);
    if (!computeColorsDiff(current.colors, nextColors)) return;
    const { past, pastTargets } = captureSiteHistory(current, null);
    const nextDraft = { ...current, colors: nextColors };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistDesign(nextDraft, current);
  },

  setPersistedCustomColors: (persistedCustomColors) =>
    set({
      draft: {
        ...get().draft,
        persistedCustomColors: normalizeComparableColors(persistedCustomColors),
      },
    }),

  initializeFonts: (fonts) =>
    set({
      original: { ...get().original, fonts },
      draft: { ...get().draft, fonts },
    }),

  setDraftFonts: (fonts) => {
    const current = get().draft;
    if (!computeJsonChanged(current.fonts, fonts)) return;
    const { past, pastTargets } = captureSiteHistory(current, null);
    const nextDraft = { ...current, fonts };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistDesign(nextDraft, current);
  },

  initializeHeader: (header) =>
    set({
      original: { ...get().original, header },
      draft: { ...get().draft, header },
    }),

  setDraftHeader: (header) => {
    const current = get().draft;
    const nextHeader = normalizeSiteHeader(header);
    if (!computeJsonChanged(normalizeSiteHeader(current.header), nextHeader)) return;
    const { past, pastTargets } = captureSiteHistory(
      current,
      PAGE_EDITOR_HISTORY_TARGET_IDS.header,
    );
    const nextDraft = { ...current, header: nextHeader };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistSlot('header', nextHeader);
  },

  initializeFooter: (footer) =>
    set({
      original: { ...get().original, footer },
      draft: { ...get().draft, footer },
    }),

  setDraftFooter: (footer) => {
    const current = get().draft;
    const nextFooter = normalizeSiteFooter(footer);
    if (!computeJsonChanged(normalizeSiteFooter(current.footer), nextFooter)) return;
    const { past, pastTargets } = captureSiteHistory(
      current,
      PAGE_EDITOR_HISTORY_TARGET_IDS.footer,
    );
    const nextDraft = { ...current, footer: nextFooter };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistSlot('footer', nextFooter);
  },

  updateDraftFooter: (changes) => {
    const footerSlot = usePageEditorStore.getState().page?.slots?.footer;
    const base =
      get().draft.footer ??
      (footerSlot
        ? {
            variant: (footerSlot.variant as FooterSlotConfig['variant']) ?? 'default',
            elements: normalizeFooterElements(footerSlot.elements),
            settings: normalizeFooterSettings(footerSlot.settings),
            socialLinks: footerSlot.socialLinks ?? [],
            text: footerSlot.text,
            socialMediaHeading: footerSlot.socialMediaHeading,
          }
        : null);
    if (!base) return;
    get().setDraftFooter({ ...base, ...changes });
  },

  initializeButtons: (buttons) =>
    set({
      original: { ...get().original, buttons },
      draft: { ...get().draft, buttons },
    }),

  setDraftButtons: (buttons) => {
    const current = get().draft;
    if (!computeJsonChanged(current.buttons, buttons)) return;
    const { past, pastTargets } = captureSiteHistory(current, null);
    const nextDraft = { ...current, buttons };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistDesign(nextDraft, current);
  },

  initializeSpecialPageLayouts: (layouts) =>
    set({
      original: { ...get().original, specialPageLayouts: layouts },
      draft: { ...get().draft, specialPageLayouts: layouts },
    }),

  setSpecialPageLayout: (pageType, code) => {
    const current = get().draft;
    const key = layoutKeyFor(pageType);
    const currentCode = current.specialPageLayouts?.[key] ?? null;
    if (currentCode === code) return;
    const { past, pastTargets } = captureSiteHistory(current, null);
    const nextDraft: SiteStyles = {
      ...current,
      specialPageLayouts: {
        caseResult: current.specialPageLayouts?.caseResult ?? null,
        blog: current.specialPageLayouts?.blog ?? null,
        [key]: code,
      },
    };
    set({
      draft: nextDraft,
      historyPast: past,
      historyPastTargets: pastTargets,
      historyFuture: [],
      historyFutureTargets: [],
    });
    persistSpecialPageLayout(pageType, code);
  },

  commitChanges: () => {
    const { draft } = get();
    set({
      original: cloneStyles(draft),
      historyPast: [],
      historyFuture: [],
      historyPastTargets: [],
      historyFutureTargets: [],
    });
  },

  setIsSaving: (saving) => set({ isSaving: saving }),

  hasUnsavedChanges: () => {
    const { original, draft } = get();
    return computeHasUnsavedChanges(original, draft);
  },
}));

if (isEditorWindow) {
  registerSiteHistoryProvider(() => {
    const state = useSiteStylesStore.getState();
    return {
      historyPast: state.historyPast,
      historyFuture: state.historyFuture,
      historyPastTargets: state.historyPastTargets,
      historyFutureTargets: state.historyFutureTargets,
    };
  });
  useSiteStylesStore.subscribe(() => scheduleEditorHistoryWrite());
  registerSiteHistoryApplier((persisted) => {
    useSiteStylesStore.setState((state) => ({
      ...state,
      historyPast: persisted.historyPast,
      historyFuture: persisted.historyFuture,
      historyPastTargets: persisted.historyPastTargets,
      historyFutureTargets: persisted.historyFutureTargets,
    }));
  });
}

// Normalize each button so a revert matches whether it came raw (server) or filled (editor popover).
// Match the editor's position-based style default (primary/secondary) so an unset style compares equal.
const normalizeComparableButton = (
  button: SiteHeaderSlotConfig['primaryButton'],
  defaultStyle: 'primary' | 'secondary',
) => (button ? normalizeButtonConfig(button, { style: defaultStyle }) : null);

const extractHeaderButtons = (header: SiteHeaderSlotConfig | null) => ({
  primaryButton: normalizeComparableButton(header?.primaryButton, 'primary'),
  secondaryButton: normalizeComparableButton(header?.secondaryButton, 'secondary'),
});

const extractHeaderWithoutButtons = (header: SiteHeaderSlotConfig | null) => {
  if (!header) return null;
  const { primaryButton: _primaryButton, secondaryButton: _secondaryButton, ...rest } = header;
  return rest;
};

const computeColorsDiff = (a: ColorSettings | null, b: ColorSettings | null): boolean => {
  if (a === null && b === null) return false;
  if (a === null || b === null) return true;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).some(
    (key) => a[key as keyof ColorSettings] !== b[key as keyof ColorSettings],
  );
};

const computeButtonsDiff = (a: ButtonSettings | null, b: ButtonSettings | null): boolean => {
  if (a === null && b === null) return false;
  if (a === null || b === null) return true;
  return a.style !== b.style || a.shape !== b.shape;
};

// Drop pairingName metadata (flipped to 'custom' on edit, never restored) and fill defaults so a revert matches.
const normalizeComparableFonts = (fonts: FontSettings | null) => ({
  headings: {
    font: fonts?.headings?.font || DEFAULT_HEADING_FONT.font,
    weight: fonts?.headings?.weight || DEFAULT_HEADING_FONT.weight,
    capitalization: fonts?.headings?.capitalization || DEFAULT_HEADING_FONT.capitalization,
  },
  paragraphs: {
    font: fonts?.paragraphs?.font || DEFAULT_PARAGRAPH_FONT.font,
    weight: fonts?.paragraphs?.weight || DEFAULT_PARAGRAPH_FONT.weight,
    size: fonts?.paragraphs?.size || DEFAULT_PARAGRAPH_FONT.size,
  },
});

const computeFontsDiff = (a: FontSettings | null, b: FontSettings | null): boolean => {
  if (a === null && b === null) return false;
  if (a === null || b === null) return true;
  return computeJsonChanged(normalizeComparableFonts(a), normalizeComparableFonts(b));
};

// Normalize both sides so a revert matches whether the stored value was raw (initialize*) or normalized (setDraft*).
const computeChangedCategories = (original: SiteStyles, draft: SiteStyles): string[] => {
  const originalHeader = normalizeSiteHeader(original.header);
  const draftHeader = normalizeSiteHeader(draft.header);

  const logoChanged = computeJsonChanged(
    normalizeComparableLogo(original.logo),
    normalizeComparableLogo(draft.logo),
  );
  const taglineChanged = original.logoTagline !== draft.logoTagline;
  const colorsChanged = computeColorsDiff(
    normalizeComparableColors(original.colors),
    normalizeComparableColors(draft.colors),
  );
  const customColorsChanged = computeColorsDiff(
    normalizeComparableColors(original.persistedCustomColors),
    normalizeComparableColors(draft.persistedCustomColors),
  );
  const fontsChanged = computeFontsDiff(original.fonts, draft.fonts);
  const ctaButtonsChanged = computeJsonChanged(
    extractHeaderButtons(originalHeader),
    extractHeaderButtons(draftHeader),
  );
  const headerChanged = computeJsonChanged(
    extractHeaderWithoutButtons(originalHeader),
    extractHeaderWithoutButtons(draftHeader),
  );
  const footerChanged = computeJsonChanged(
    normalizeSiteFooter(original.footer),
    normalizeSiteFooter(draft.footer),
  );
  const buttonsChanged = computeButtonsDiff(original.buttons, draft.buttons);

  const categories: string[] = [];
  if (headerChanged) categories.push('header');
  if (footerChanged) categories.push('footer');
  if (logoChanged || taglineChanged) categories.push('logo');
  if (ctaButtonsChanged) categories.push('cta_buttons');
  if (colorsChanged || customColorsChanged || fontsChanged || buttonsChanged)
    categories.push('styles');
  return categories;
};

const computeHasUnsavedChanges = (original: SiteStyles, draft: SiteStyles): boolean =>
  computeChangedCategories(original, draft).length > 0;

const resolveSiteHistoryHighlightTargetId = (
  previousDraft: SiteStyles,
  nextDraft: SiteStyles,
): string | null => {
  if (
    computeJsonChanged(previousDraft.header, nextDraft.header) ||
    computeJsonChanged(previousDraft.logo, nextDraft.logo)
  ) {
    return PAGE_EDITOR_HISTORY_TARGET_IDS.header;
  }
  if (computeJsonChanged(previousDraft.footer, nextDraft.footer)) {
    return PAGE_EDITOR_HISTORY_TARGET_IDS.footer;
  }
  return null;
};

// Adapter exposed to page-editor-store so its unified undo/redo can dispatch
// site-history entries chronologically with page edits.
export const siteHistoryAdapter = {
  canUndo: () => useSiteStylesStore.getState().historyPast.length > 0,
  canRedo: () => useSiteStylesStore.getState().historyFuture.length > 0,
  clearFuture: () => {
    if (useSiteStylesStore.getState().historyFuture.length === 0) return;
    useSiteStylesStore.setState({ historyFuture: [], historyFutureTargets: [] });
  },
  undo: () => {
    const state = useSiteStylesStore.getState();
    if (state.historyPast.length === 0) return false;
    const previous = state.historyPast[state.historyPast.length - 1];
    const originTarget = state.historyPastTargets[state.historyPastTargets.length - 1] ?? null;
    const currentSnapshot = cloneStyles(state.draft);
    useSiteStylesStore.setState({
      draft: cloneStyles(previous),
      historyPast: state.historyPast.slice(0, -1),
      historyPastTargets: state.historyPastTargets.slice(0, -1),
      historyFuture: trimSiteHistory([...state.historyFuture, currentSnapshot]),
      historyFutureTargets: [...state.historyFutureTargets, originTarget].slice(
        -SITE_HISTORY_LIMIT,
      ),
    });
    persistDraft(previous, currentSnapshot);
    const historyTargetId =
      originTarget ?? resolveSiteHistoryHighlightTargetId(currentSnapshot, previous);
    if (historyTargetId) {
      usePageEditorStore.getState().markHistoryHighlightTarget(historyTargetId);
    }
    return true;
  },
  redo: () => {
    const state = useSiteStylesStore.getState();
    if (state.historyFuture.length === 0) return false;
    const next = state.historyFuture[state.historyFuture.length - 1];
    const originTarget = state.historyFutureTargets[state.historyFutureTargets.length - 1] ?? null;
    const currentSnapshot = cloneStyles(state.draft);
    useSiteStylesStore.setState({
      draft: cloneStyles(next),
      historyPast: trimSiteHistory([...state.historyPast, currentSnapshot]),
      historyPastTargets: [...state.historyPastTargets, originTarget].slice(-SITE_HISTORY_LIMIT),
      historyFuture: state.historyFuture.slice(0, -1),
      historyFutureTargets: state.historyFutureTargets.slice(0, -1),
    });
    persistDraft(next, currentSnapshot);
    const historyTargetId =
      originTarget ?? resolveSiteHistoryHighlightTargetId(currentSnapshot, next);
    if (historyTargetId) {
      usePageEditorStore.getState().markHistoryHighlightTarget(historyTargetId);
    }
    return true;
  },
};

export const useSiteStylesActions = () =>
  useSiteStylesStore(
    useShallow((state) => ({
      initializeColors: state.initializeColors,
      initializeLogo: state.initializeLogo,
      initializeLogoTagline: state.initializeLogoTagline,
      setDraftLogo: state.setDraftLogo,
      setDraftLogoTagline: state.setDraftLogoTagline,
      initializePersistedCustomColors: state.initializePersistedCustomColors,
      setDraftColors: state.setDraftColors,
      setPersistedCustomColors: state.setPersistedCustomColors,
      initializeFonts: state.initializeFonts,
      setDraftFonts: state.setDraftFonts,
      initializeHeader: state.initializeHeader,
      setDraftHeader: state.setDraftHeader,
      initializeFooter: state.initializeFooter,
      setDraftFooter: state.setDraftFooter,
      initializeButtons: state.initializeButtons,
      setDraftButtons: state.setDraftButtons,
      initializeSpecialPageLayouts: state.initializeSpecialPageLayouts,
      commitChanges: state.commitChanges,
      setIsSaving: state.setIsSaving,
    })),
  );

export const useSiteStylesState = () =>
  useSiteStylesStore(
    useShallow((state) => ({
      draftLogo: state.draft.logo,
      draftLogoTagline: state.draft.logoTagline,
      draftColors: state.draft.colors,
      persistedCustomColors: state.draft.persistedCustomColors,
      draftFonts: state.draft.fonts,
      draftHeader: state.draft.header,
      draftFooter: state.draft.footer,
      draftButtons: state.draft.buttons,
      isSaving: state.isSaving,
      hasUnsavedChanges: state.hasUnsavedChanges(),
    })),
  );
